import * as Sentry from '@sentry/react'

/**
 * Thin wrapper over the Sentry structured Logs API (Sentry.logger.*).
 *
 * Use this for intentional, queryable application logs — distinct from:
 *  - error capture (thrown exceptions -> Issues), and
 *  - console capture (console.warn/error -> logs, via consoleLoggingIntegration).
 *
 * Every log automatically carries the authenticated user (set on the Sentry
 * scope in app.tsx) and the deploy `environment`. We also stamp an explicit
 * `app` + `environment` attribute so logs are easy to filter in the Sentry
 * Logs UI (Explore -> Logs) regardless of org-wide views.
 */
const ENVIRONMENT =
  import.meta.env.VITE_SENTRY_ENVIRONMENT ||
  import.meta.env.MODE ||
  'development'

type LogAttributes = Record<string, unknown>

function withBase(attributes?: LogAttributes): LogAttributes {
  return { app: 'agenthub-client', environment: ENVIRONMENT, ...attributes }
}

export const log = {
  debug: (message: string, attributes?: LogAttributes) =>
    Sentry.logger.debug(message, withBase(attributes)),
  info: (message: string, attributes?: LogAttributes) =>
    Sentry.logger.info(message, withBase(attributes)),
  warn: (message: string, attributes?: LogAttributes) =>
    Sentry.logger.warn(message, withBase(attributes)),
  error: (message: string, attributes?: LogAttributes) =>
    Sentry.logger.error(message, withBase(attributes)),
}
